import React, { useState, useCallback, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import classnames from 'classnames'
import pickBy from 'lodash/pickBy'
import isEqual from 'lodash/isEqual'
import Return from '@Images/languages_view_return.svg'
import Button from '@SiteComponents/Button'
import StepIndicator from '@SiteComponents/StepIndicator'
import S3Remover from '@Utils/S3Remover'
import { abortUpload } from '@Utils/S3Storage'
import Continue from '@Images/new_project_popup_continue.svg'
import List from '@Images/projects_table_header_list.svg'
import Mic from '@Images/start_recording_audio.svg'

import SpeakerVoiceThumbnail, { type IFileToUpload as IThumbnailFileToUpload } from '@SiteComponents/SpeakerVoiceThumbnail'
import SpeakerVoiceData from '@SiteComponents/SpeakerVoiceData'
import TrainingSamples, { type IFileToUpload as ITrainingFileToUpload } from '@SiteComponents/TrainingSamples'
import FormDropdown, { type FormDropdownOption } from '@SiteComponents/FormDropdown'
import { ODUB_DESIGN_VOICE_CLONE_MODEL_LABEL } from '@Utils/OdubTtsModels'
import DesignVoiceSampleResultCard from './DesignVoiceSampleResultCard'

import {
  updateSpeakerVoice,
  removeSpeakerVoicesFromWorkspace,
  generateDesignSample,
  resetGeneratedDesignSample,
} from '@SiteModules/SpeakerVoice/actions'

import {
  ISpeakerVoice,
  IDraftSpeakerVoice,
} from '@SiteModules/SpeakerVoice/types'

import {
  getLanguagesNamesAndCountries,
  getGeneratedDesignSampleError,
  getGeneratedDesignSampleUrl,
  getIsGeneratingDesignSample,
} from '@SiteModules/SpeakerVoice/selectors'

import './SpeakerVoiceSidePanelContent.scss'

type VoiceStudioCreationMode = 'clone' | 'design'

const DESIGN_VOICE_SAMPLE_TEXTS: Record<string, string> = {
  Auto: 'Great stories deserve to be heard everywhere. I\'m here to bring your next video to life with perfect emotion.',
  English: 'Great stories deserve to be heard everywhere. I\'m here to bring your next video to life with perfect emotion.',
  French: 'Les grandes histoires méritent d\'être entendues partout. Je suis là pour donner vie à votre prochaine vidéo avec l\'émotion parfaite.',
  German: 'Großartige Geschichten verdienen es, überall gehört zu werden. Ich bin hier, um Ihrem nächsten Video mit der perfekten Emotion Leben einzuhauchen.',
  Spanish: 'Las grandes historias merecen ser escuchadas en todas partes. Estoy aquí para darle vida a tu próximo video con la emoción perfecta.',
  Portuguese: 'Grandes histórias merecem ser ouvidas em todo lugar. Estou aqui para dar vida ao seu próximo vídeo com a emoção perfeita.',
  Italian: 'Le grandi storie meritano di essere ascoltate ovunque. Sono qui per dare vita al tuo prossimo video con l\'emozione perfetta.',
  Russian: 'Великие истории заслуживают того, чтобы их услышали везде. Я здесь, чтобы оживить ваше следующее видео с идеальной эмоцией.',
  Japanese: '素晴らしい物語は、世界中で聞かれるべきです。完璧な感情を込めて、あなたの次の動画に命を吹き込みます。',
  Korean: '위대한 이야기는 어디서든 들려야 합니다. 완벽한 감정으로 당신의 다음 영상에 생기를 불어넣기 위해 제가 왔습니다.',
  Chinese: '伟大的故事值得被全世界听到。我在这里，用最完美的情感为你的下一个视频注入生命。',
}

const DEFAULT_DESIGN_SAMPLE_TEXT = DESIGN_VOICE_SAMPLE_TEXTS.Auto

const DESIGN_VOICE_LANGUAGE_OPTIONS: FormDropdownOption<string>[] = [
  { value: 'Auto', label: 'Auto', description: 'Auto-detect' },
  { value: 'English', label: 'English', description: 'Design' },
  { value: 'French', label: 'French', description: 'Design' },
  { value: 'German', label: 'German', description: 'Design' },
  { value: 'Spanish', label: 'Spanish', description: 'Design' },
  { value: 'Portuguese', label: 'Portuguese', description: 'Design' },
  { value: 'Italian', label: 'Italian', description: 'Design' },
  { value: 'Russian', label: 'Russian', description: 'Design' },
  { value: 'Japanese', label: 'Japanese', description: 'Design' },
  { value: 'Korean', label: 'Korean', description: 'Design' },
  { value: 'Chinese', label: 'Chinese', description: 'Design' },
]

interface VoicePromptPreset {
  readonly category: 'energetic' | 'professional' | 'storyteller'
  readonly prompt: string
}

const VOICE_PROMPT_PRESETS: VoicePromptPreset[] = [
  {
    category: 'energetic',
    prompt: 'A young female voice with an American accent. Highly energetic, enthusiastic, and fast-paced, with a dynamic pitch. Perfect for a viral social media hook.',
  },
  {
    category: 'energetic',
    prompt: 'A young male voice, upbeat and high-energy, with a confident American accent. Speaks fast with excitement and natural emphasis. Ideal for gaming or tech content.',
  },
  {
    category: 'energetic',
    prompt: 'A teenage female voice bursting with excitement. Bright, animated, and playful with natural warmth in the tone. Great for lifestyle or entertainment content.',
  },
  {
    category: 'energetic',
    prompt: 'A young male voice with a relaxed, conversational tone. Friendly, approachable, and slightly upbeat, as if chatting with a friend. Clear studio audio.',
  },
  {
    category: 'professional',
    prompt: 'A middle-aged male voice with a British accent. The tone is calm, authoritative, and professional, speaking at a measured pace with perfect articulation.',
  },
  {
    category: 'professional',
    prompt: 'A confident female voice with a neutral accent. Clear, composed, and precise delivery with a warm but corporate tone. Perfect for training videos and presentations.',
  },
  {
    category: 'professional',
    prompt: 'A mature male voice with a deep resonance. Steady, trustworthy, and polished, with a neutral American accent. Ideal for financial or legal narration.',
  },
  {
    category: 'professional',
    prompt: 'A mature female voice with a neutral, mid-Atlantic accent. The delivery is serious, objective, crisp, and highly articulate. No background noise.',
  },
  {
    category: 'storyteller',
    prompt: 'An elderly male voice with a deep, warm, and slightly raspy timbre. The pacing is slow with dramatic pauses, evoking a comforting and nostalgic mood.',
  },
  {
    category: 'storyteller',
    prompt: 'A soft-spoken female voice with a gentle Irish lilt. Warm, soothing, and melodic with an intimate whisper quality, as if reading a bedtime story.',
  },
  {
    category: 'storyteller',
    prompt: 'A rich baritone voice with theatrical flair. Deliberate pacing, vivid emphasis on key words, and a sense of mystery. Perfect for documentary narration.',
  },
  {
    category: 'storyteller',
    prompt: 'A warm, mid-range female voice with a slight French accent. Elegant, sophisticated, and smooth, with measured pacing and a hint of playfulness.',
  },
]

interface DesignVoiceSampleSlotProps {
  readonly url: string
  readonly index: number
  readonly regenerateDisabled: boolean
  onRemoveUrl(url: string): void
  onRegenerateUrl(url: string): void
}

const DesignVoiceSampleSlot: React.FC<DesignVoiceSampleSlotProps> = props => {
  const handleRemove = () => {
    props.onRemoveUrl(props.url)
  }

  const handleRegenerate = () => {
    props.onRegenerateUrl(props.url)
  }

  const fileName = props.url.split('/').pop() || `Sample ${props.index + 1}`

  return (
    <DesignVoiceSampleResultCard
      url={props.url}
      label={fileName}
      onRemove={handleRemove}
      onRegenerate={handleRegenerate}
      regenerateDisabled={props.regenerateDisabled}
    />
  )
}

interface SpeakerVoiceSidePanelContentProps {
  readonly fullSpeakerVoiceSidePanel: boolean
  readonly isNewSpeakerVoice: boolean
  readonly workspaceId: number
  readonly speakerVoice?: ISpeakerVoice
  resetCurrentSpeakerVoice(): void
  resetIsNewCurrentSpeakerVoice(): void
  onSpeakerVoiceRemoval(speakerVoiceId: string): void
}

const getInitialSelectedTab = (isNewSpeakerVoice: boolean, speakerVoice?: ISpeakerVoice) => {
  const isPromptedDesignVoice = speakerVoice?.attributes.service === 'odub' && speakerVoice?.attributes.odubModelId === 'prompted'
  return (isNewSpeakerVoice || isPromptedDesignVoice) ? 'samples' : 'information'
}

const SpeakerVoiceSidePanelContent: React.FC<SpeakerVoiceSidePanelContentProps> = props => {
  const dispatch = useDispatch()

  const {
    workspaceId,
    speakerVoice,
    fullSpeakerVoiceSidePanel,
    isNewSpeakerVoice,
  } = props

  const languagesNamesAndCountries = useSelector(getLanguagesNamesAndCountries)
  const generatedDesignSampleUrl = useSelector(getGeneratedDesignSampleUrl)
  const generatedDesignSampleError = useSelector(getGeneratedDesignSampleError)
  const isGeneratingDesignSample = useSelector(getIsGeneratingDesignSample)

  const [selectedTab, setSelectedTab] = useState(getInitialSelectedTab(isNewSpeakerVoice, speakerVoice))
  const [isDesignMode, setIsDesignMode] = useState(false)
  const [voiceStudioCreationMode, setVoiceStudioCreationMode] = useState<VoiceStudioCreationMode>('design')
  const [designSampleTestText, setDesignSampleTestText] = useState(DEFAULT_DESIGN_SAMPLE_TEXT)

  const [thumbnailFileToUpload, setThumbnailFileToUpload] = useState<IThumbnailFileToUpload | null>(null)
  const thumbnailFileToUploadRef = useRef<IThumbnailFileToUpload | null>(null)
  thumbnailFileToUploadRef.current = thumbnailFileToUpload
  const [trainingFilesToUpload, setTrainingFilesToUpload] = useState<ITrainingFileToUpload[]>([])
  const trainingFilesToUploadRef = useRef<ITrainingFileToUpload[]>([])
  trainingFilesToUploadRef.current = trainingFilesToUpload
  const previousSpeakerVoiceRef = useRef<ISpeakerVoice | undefined>(speakerVoice)
  const previousIsNewSpeakerVoiceRef = useRef<boolean>(isNewSpeakerVoice)

  const cleanupPendingUploads = useCallback(async (): Promise<boolean> => {
    const pendingTrainingFiles = trainingFilesToUploadRef.current
    const pendingThumbnailFile = thumbnailFileToUploadRef.current
    const abortPromises: Promise<boolean>[] = []
    const removalPromises: Promise<void>[] = []

    pendingTrainingFiles.forEach(fileToUpload => {
      if (fileToUpload.url) {
        const remover = new S3Remover({
          fileUrl: fileToUpload.url,
        })
        removalPromises.push(remover.call())
      } else if (fileToUpload.uploadingRequest) {
        abortPromises.push(abortUpload(fileToUpload.uploadingRequest))
      }
    })

    if (pendingThumbnailFile) {
      if (pendingThumbnailFile.url) {
        const remover = new S3Remover({
          fileUrl: pendingThumbnailFile.url,
        })
        removalPromises.push(remover.call())
      } else if (pendingThumbnailFile.uploadingRequest) {
        abortPromises.push(abortUpload(pendingThumbnailFile.uploadingRequest))
      }
    }

    const abortResults = await Promise.all(abortPromises)
    const uploadsAborted = abortResults.every(Boolean)
    if (!uploadsAborted) { return false }

    setTrainingFilesToUpload(currentFiles => currentFiles === pendingTrainingFiles ? [] : currentFiles)
    setThumbnailFileToUpload(currentFile => currentFile === pendingThumbnailFile ? null : currentFile)
    void Promise.all(removalPromises).catch(() => undefined)
    return true
  }, [])

  const cleanupBeforeUnload = useCallback(async () => {
    const cleanupCompleted = await cleanupPendingUploads()
    if (!cleanupCompleted) { return }

    if (previousIsNewSpeakerVoiceRef.current && previousSpeakerVoiceRef.current?.id) {
      dispatch(removeSpeakerVoicesFromWorkspace({ workspaceId, speakerVoiceIds: [previousSpeakerVoiceRef.current.id], preventNotification: true }))
    }
  }, [cleanupPendingUploads])

  const buildInitialDraft = useCallback((): IDraftSpeakerVoice => {
    const attributes = speakerVoice?.attributes

    return {
      id: speakerVoice?.id || '',
      color: attributes?.color || '#83cbfb', // Random color that should never be visible
      name: attributes?.name,
      private: attributes?.private,
      languageName: attributes?.languageName,
      languageAccent: attributes?.languageAccent,
      description: attributes?.description,
      gender: attributes?.gender,
      ageGroup: attributes?.ageGroup,
      thumbnailUrl: attributes?.thumbnailUrl,
      trainingUrls: attributes?.trainingUrls,
      service: attributes?.service,
      odubModelId: attributes?.odubModelId,
    }
  }, [speakerVoice, workspaceId])

  const [originalSpeakerVoiceState, setOriginalSpeakerVoiceState] = useState<IDraftSpeakerVoice>(buildInitialDraft())
  const [draftSpeakerVoice, setDraftSpeakerVoice] = useState<IDraftSpeakerVoice>(buildInitialDraft())
  const isSamplesTabSelected = selectedTab === 'samples'
  const isInformationTabSelected = selectedTab === 'information'
  const isPromptedDesignVoice = draftSpeakerVoice.service === 'odub' && draftSpeakerVoice.odubModelId === 'prompted'
  const shouldEnableDesignGenerator = isDesignMode || isPromptedDesignVoice
  const isDesignCreationFirstStep = isNewSpeakerVoice && isSamplesTabSelected && voiceStudioCreationMode === 'design'
  const isDesignEditSamplesView = !isNewSpeakerVoice && isSamplesTabSelected && isPromptedDesignVoice
  const shouldHideStandaloneDescriptionField = (isNewSpeakerVoice && !isSamplesTabSelected && voiceStudioCreationMode === 'design') || (!isNewSpeakerVoice && isPromptedDesignVoice)
  const shouldShowInlineDesignGenerator = shouldEnableDesignGenerator && !shouldHideStandaloneDescriptionField
  const anythingChanged = JSON.stringify(originalSpeakerVoiceState) !== JSON.stringify(draftSpeakerVoice)
  const areSomeFilesUploading = trainingFilesToUpload.some(file => !file.hasCompletedUpload)
  const firstStepBlocksContinue = isDesignCreationFirstStep
    ? (
      !draftSpeakerVoice.description?.trim()
      || (draftSpeakerVoice.trainingUrls?.length ?? 0) === 0
    )
    : (draftSpeakerVoice.trainingUrls?.length === 0 || areSomeFilesUploading)
  const shouldDisableContinue = isNewSpeakerVoice && isSamplesTabSelected
    ? (firstStepBlocksContinue || !anythingChanged)
    : (draftSpeakerVoice.trainingUrls?.length === 0 || areSomeFilesUploading || !anythingChanged)

  useEffect(() => {
    const handleBeforeUnload = () => {
      void cleanupBeforeUnload()
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      void cleanupBeforeUnload()
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [])

  useEffect(() => {
    setOriginalSpeakerVoiceState(buildInitialDraft())
    setDraftSpeakerVoice(buildInitialDraft())
  }, [buildInitialDraft])

  useEffect(() => {
    setSelectedTab(getInitialSelectedTab(isNewSpeakerVoice, speakerVoice))
    if (isNewSpeakerVoice) {
      setVoiceStudioCreationMode('design')
      setIsDesignMode(true)
    } else {
      setIsDesignMode(false)
    }
    dispatch(resetGeneratedDesignSample())

    const previousSpeakerVoice = previousSpeakerVoiceRef.current
    const previousIsNewSpeakerVoice = previousIsNewSpeakerVoiceRef.current

    const hasSpeakerVoiceChanged = previousSpeakerVoice?.id !== speakerVoice?.id

    if (hasSpeakerVoiceChanged && previousSpeakerVoice) {
      const cleanupPreviousSpeakerVoice = async () => {
        const cleanupCompleted = await cleanupPendingUploads()
        if (!cleanupCompleted) { return }

        if (previousIsNewSpeakerVoice && previousSpeakerVoice.id) {
          dispatch(removeSpeakerVoicesFromWorkspace({ workspaceId, speakerVoiceIds: [previousSpeakerVoice.id] }))
        }
      }

      void cleanupPreviousSpeakerVoice()
    }

    previousSpeakerVoiceRef.current = speakerVoice
    previousIsNewSpeakerVoiceRef.current = isNewSpeakerVoice
  }, [cleanupPendingUploads, dispatch, isNewSpeakerVoice, speakerVoice, workspaceId])

  useEffect(() => {
    setDesignSampleTestText(DEFAULT_DESIGN_SAMPLE_TEXT)
  }, [speakerVoice?.id])

  useEffect(() => {
    if (!generatedDesignSampleUrl) { return }

    setDraftSpeakerVoice(prev => {
      const existingUrls = prev.trainingUrls || []

      if (existingUrls.includes(generatedDesignSampleUrl)) { return prev }

      return { ...prev, trainingUrls: [generatedDesignSampleUrl, ...existingUrls] }
    })

    dispatch(resetGeneratedDesignSample())
  }, [dispatch, generatedDesignSampleUrl])

  const shouldRender = !!speakerVoice

  const updateSpeakerVoiceFromDraft = () => {
    const changedFields = pickBy(draftSpeakerVoice, (value, key) => !isEqual(value, originalSpeakerVoiceState[key as keyof IDraftSpeakerVoice]))
    dispatch(updateSpeakerVoice({ id: draftSpeakerVoice.id, ...changedFields }))
    setTrainingFilesToUpload([])
    setThumbnailFileToUpload(null)
    props.resetIsNewCurrentSpeakerVoice()
  }

  const handleEditHeaderButtonClick = () => {
    props.onSpeakerVoiceRemoval(speakerVoice!.id)
  }

  const handleDraftChange = (updatedFields: Partial<IDraftSpeakerVoice>) => {
    setDraftSpeakerVoice(prev => ({
      ...prev,
      ...updatedFields,
    }))
  }

  useEffect(() => {
    if (!isNewSpeakerVoice || !speakerVoice?.id) { return }
    if (voiceStudioCreationMode !== 'design') { return }

    setDraftSpeakerVoice(prev => {
      if (prev.id !== speakerVoice.id) { return prev }
      if (prev.service === 'odub' && prev.odubModelId === 'prompted') { return prev }

      return { ...prev, service: 'odub', odubModelId: 'prompted' }
    })
  }, [isNewSpeakerVoice, speakerVoice?.id, voiceStudioCreationMode])

  const handleSelectCloneVoiceMode = () => {
    setVoiceStudioCreationMode('clone')
    setIsDesignMode(false)
    handleDraftChange({ service: undefined, odubModelId: undefined, description: undefined })
  }

  const handleSelectDesignVoiceMode = () => {
    setVoiceStudioCreationMode('design')
    setIsDesignMode(true)
    dispatch(resetGeneratedDesignSample())
    handleDraftChange({ service: 'odub', odubModelId: 'prompted' })
  }

  const handleDesignStepVoiceDescriptionChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    handleDraftChange({ description: event.target.value })
  }

  const handleDesignStepVoiceDescriptionKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    event.stopPropagation()
  }

  const handleInspireMe = () => {
    const currentDescription = draftSpeakerVoice.description?.trim() ?? ''
    const candidates = VOICE_PROMPT_PRESETS.filter(p => p.prompt !== currentDescription)
    const pool = candidates.length > 0 ? candidates : VOICE_PROMPT_PRESETS
    const selected = pool[Math.floor(Math.random() * pool.length)]
    handleDraftChange({ description: selected.prompt })
  }

  const handlePromptChipClick = (category: string) => {
    const categoryPrompts = VOICE_PROMPT_PRESETS.filter(p => p.category === category)
    const currentDescription = draftSpeakerVoice.description?.trim() ?? ''
    const candidates = categoryPrompts.filter(p => p.prompt !== currentDescription)
    const pool = candidates.length > 0 ? candidates : categoryPrompts
    if (pool.length === 0) { return }
    const selected = pool[Math.floor(Math.random() * pool.length)]
    handleDraftChange({ description: selected.prompt })
  }

  const handleCreateContinue = () => {
    if (selectedTab === 'samples') {
      if (voiceStudioCreationMode === 'design' && draftSpeakerVoice.languageName === 'Auto') {
        handleDraftChange({ languageName: undefined })
      }
      setSelectedTab('information')
    } else {
      updateSpeakerVoiceFromDraft()
    }
  }

  const handleRemoveGeneratedSample = (urlToRemove: string) => {
    const updatedUrls = (draftSpeakerVoice.trainingUrls || []).filter(url => url !== urlToRemove)
    handleDraftChange({ trainingUrls: updatedUrls })
  }

  const handleRemoveDesignSampleUrl = (url: string) => {
    const originalTrainingUrls = originalSpeakerVoiceState.trainingUrls || []
    const removeFromDraft = () => {
      handleRemoveGeneratedSample(url)
    }

    if (!originalTrainingUrls.includes(url)) {
      const remover = new S3Remover({ fileUrl: url })
      void remover.call().then(removeFromDraft).catch(() => undefined)
    } else {
      removeFromDraft()
    }
  }

  const handleDesignLanguageChange = (nextLanguage: string | null) => {
    handleDraftChange({
      languageName: nextLanguage ?? undefined,
      languageAccent: undefined,
    })
    const resolvedLanguageKey = nextLanguage != null ? nextLanguage : 'Auto'
    const sampleText = DESIGN_VOICE_SAMPLE_TEXTS[resolvedLanguageKey] ?? DESIGN_VOICE_SAMPLE_TEXTS.Auto
    setDesignSampleTestText(sampleText)
  }

  const handleDesignSampleTextChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setDesignSampleTestText(event.target.value)
  }

  const handleDesignSampleTextKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    event.stopPropagation()
  }

  const handleClickGenerateVoiceSample = () => {
    handleGenerateDesignSample(designSampleTestText)
  }

  const handleRegenerateDesignSample = (urlToRemove: string) => {
    const originalTrainingUrls = originalSpeakerVoiceState.trainingUrls || []

    const applyRegenerate = () => {
      setDraftSpeakerVoice(prev => {
        const nextTrainingUrls = (prev.trainingUrls || []).filter(u => u !== urlToRemove)
        const description = prev.description?.trim()
        const text = designSampleTestText.trim()

        if (description && text) {
          queueMicrotask(() => {
            dispatch(generateDesignSample({
              workspaceId,
              text,
              description,
              languageName: prev.languageName === 'Auto' ? undefined : prev.languageName,
            }))
          })
        }

        return { ...prev, trainingUrls: nextTrainingUrls }
      })
    }

    if (!originalTrainingUrls.includes(urlToRemove)) {
      const remover = new S3Remover({ fileUrl: urlToRemove })
      void remover.call().then(applyRegenerate).catch(applyRegenerate)
    } else {
      applyRegenerate()
    }
  }

  const handleGenerateDesignSample = (textToGenerate: string) => {
    const description = draftSpeakerVoice.description?.trim()
    const text = textToGenerate.trim()

    if (!description || !text) { return }

    const languageName = draftSpeakerVoice.languageName === 'Auto' ? undefined : draftSpeakerVoice.languageName

    dispatch(generateDesignSample({
      workspaceId,
      text,
      description,
      languageName,
    }))
  }

  const renderTabSelector = () => (
    <div
      className={classnames(
        'SpeakerVoiceSidePanelContent__tab-selector',
        {
          'SpeakerVoiceSidePanelContent__tab-selector--samples': isSamplesTabSelected,
          'SpeakerVoiceSidePanelContent__tab-selector--information': isInformationTabSelected,
        }
      )}
    >
      <span className="SpeakerVoiceSidePanelContent__tab-selector-indicator" />
      <button
        type="button"
        onClick={setSelectedTab.bind(null, 'information')}
        className={classnames(
          'SpeakerVoiceSidePanelContent__tab-selector-button',
          { 'is-active': isInformationTabSelected }
        )}
      >
        <List />
        Information
      </button>
      <button
        type="button"
        onClick={setSelectedTab.bind(null, 'samples')}
        className={classnames(
          'SpeakerVoiceSidePanelContent__tab-selector-button',
          { 'is-active': isSamplesTabSelected }
        )}
      >
        <Mic />
        Audio samples
      </button>
    </div>
  )

  const renderEditHeader = () => {
    return (
      <div className="SpeakerVoiceSidePanelContent__header-edit">
        <div className="SpeakerVoiceSidePanelContent__header-edit-left">
          {fullSpeakerVoiceSidePanel && (
            <Return
              className="SpeakerVoiceSidePanelContent__return"
              onClick={props.resetCurrentSpeakerVoice}
            />
          )}
          <div className="SpeakerVoiceSidePanelContent__header-thumbnail">
            <SpeakerVoiceThumbnail
              draftSpeakerVoice={draftSpeakerVoice}
              originalSpeakerVoiceState={originalSpeakerVoiceState}
              handleDraftChange={handleDraftChange}
              fileToUpload={thumbnailFileToUpload}
              setFileToUpload={setThumbnailFileToUpload}
            />
          </div>
          <div className="SpeakerVoiceSidePanelContent__header-title-description-wrapper">
            <div className="SpeakerVoiceSidePanelContent__header-title">
              {speakerVoice?.attributes.name}
            </div>
            {isPromptedDesignVoice && (
              <div className="SpeakerVoiceSidePanelContent__header-subtitle">
                Model: {ODUB_DESIGN_VOICE_CLONE_MODEL_LABEL}
              </div>
            )}
          </div>
        </div>
        <div className="SpeakerVoiceSidePanelContent__header-edit-action">
          <Button
            caption={'Remove voice'}
            onClick={handleEditHeaderButtonClick}
            styleType="danger"
            size="small"
          />
        </div>
      </div>
    )
  }

  const renderCreateHeader = () => {
    return (
      <div className="SpeakerVoiceSidePanelContent__header-create">
        {fullSpeakerVoiceSidePanel && (
          <Return
            className="SpeakerVoiceSidePanelContent__return"
            onClick={props.resetCurrentSpeakerVoice}
          />
        )}
        <div className="SpeakerVoiceSidePanelContent__header-title-description-wrapper">
          <div className="SpeakerVoiceSidePanelContent__header-title">
            Create a new voice
          </div>
          {isSamplesTabSelected ? null : (
            <div className="SpeakerVoiceSidePanelContent__header-description">
              For best results, avoid noisy environments, use a good-quality<br/>microphone, and keep the same equipment across all recordings.
            </div>
          )}
        </div>
      </div>
    )
  }

  const renderVoiceStudioCreationToggle = () => {
    const isDesignActive = voiceStudioCreationMode === 'design'

    return (
      <React.Fragment>
        <div
          className={classnames(
            'SpeakerVoiceSidePanelContent__creation-mode-toggle',
            { 'SpeakerVoiceSidePanelContent__creation-mode-toggle--clone': !isDesignActive }
          )}
          role="group"
          aria-label="Voice creation mode"
        >
          <span className="SpeakerVoiceSidePanelContent__creation-mode-indicator" />
          <button
            type="button"
            className={classnames(
              'SpeakerVoiceSidePanelContent__creation-mode-option',
              { 'SpeakerVoiceSidePanelContent__creation-mode-option--active': isDesignActive }
            )}
            onClick={handleSelectDesignVoiceMode}
          >
            {ODUB_DESIGN_VOICE_CLONE_MODEL_LABEL}
          </button>
          <button
            type="button"
            className={classnames(
              'SpeakerVoiceSidePanelContent__creation-mode-option',
              { 'SpeakerVoiceSidePanelContent__creation-mode-option--active': !isDesignActive }
            )}
            onClick={handleSelectCloneVoiceMode}
          >
            Clone Voice
          </button>
        </div>
        <p className="SpeakerVoiceSidePanelContent__creation-mode-subtitle">
          {isDesignActive
            ? `Generate a brand new, unique AI voice from scratch with ${ODUB_DESIGN_VOICE_CLONE_MODEL_LABEL} by describing its characteristics.`
            : 'Upload a clean audio sample to create an exact digital replica of a real voice.'
          }
        </p>
      </React.Fragment>
    )
  }

  const renderDesignVoiceDescriptionPrompt = () => {
    return (
      <div className="SpeakerVoiceSidePanelContent__voice-description">
        <div className="SpeakerVoiceSidePanelContent__voice-description-field">
          <label
            className="SpeakerVoiceSidePanelContent__voice-description-label"
            htmlFor="SpeakerVoiceSidePanelContent-voice-description"
          >
            Voice Description
          </label>
          <div className="SpeakerVoiceSidePanelContent__voice-description-textarea-wrapper">
            <textarea
              id="SpeakerVoiceSidePanelContent-voice-description"
              className="SpeakerVoiceSidePanelContent__voice-description-textarea"
              placeholder="Describe the voice... e.g., A young energetic woman with a British accent."
              value={draftSpeakerVoice.description ?? ''}
              onChange={handleDesignStepVoiceDescriptionChange}
              onKeyDown={handleDesignStepVoiceDescriptionKeyDown}
              rows={6}
            />
            <button
              type="button"
              className="SpeakerVoiceSidePanelContent__inspire-button"
              onClick={handleInspireMe}
            >
              ✨ Inspire me
            </button>
          </div>
        </div>
        <p className="SpeakerVoiceSidePanelContent__voice-description-tip">
          💡 Tip: Include gender, age, accent, and emotion (e.g., calm, excited, authoritative).
        </p>
        <div className="SpeakerVoiceSidePanelContent__prompt-chips">
          <button
            type="button"
            className="SpeakerVoiceSidePanelContent__prompt-chip"
            onClick={handlePromptChipClick.bind(null, 'energetic')}
          >
            🚀 Energetic
          </button>
          <button
            type="button"
            className="SpeakerVoiceSidePanelContent__prompt-chip"
            onClick={handlePromptChipClick.bind(null, 'professional')}
          >
            📚 Professional
          </button>
          <button
            type="button"
            className="SpeakerVoiceSidePanelContent__prompt-chip"
            onClick={handlePromptChipClick.bind(null, 'storyteller')}
          >
            📖 Storyteller
          </button>
        </div>
      </div>
    )
  }

  const renderDesignVoiceFirstStep = () => {
    const trainingUrls = draftSpeakerVoice.trainingUrls || []
    const canGenerateSample = Boolean(
      draftSpeakerVoice.description?.trim()
      && designSampleTestText.trim()
      && !isGeneratingDesignSample
    )

    return (
      <React.Fragment>
        {renderDesignVoiceDescriptionPrompt()}
        <div className="SpeakerVoiceSidePanelContent__design-section">
          <span className="SpeakerVoiceSidePanelContent__design-section-title">
            Language
          </span>
          <div className="SpeakerVoiceSidePanelContent__design-language-field">
            <FormDropdown
              value={draftSpeakerVoice.languageName ?? null}
              placeholder="Select language"
              options={DESIGN_VOICE_LANGUAGE_OPTIONS}
              allowSearch
              searchPlaceholder="Search language"
              noResultsLabel="No language found"
              onChange={handleDesignLanguageChange}
              menuClassName="SpeakerVoiceSidePanelContent__language-menu"
            />
          </div>
        </div>
        <div className="SpeakerVoiceSidePanelContent__design-section SpeakerVoiceSidePanelContent__design-section--testing">
          <span className="SpeakerVoiceSidePanelContent__design-section-title">
            Preview
          </span>
          <label
            className="SpeakerVoiceSidePanelContent__design-testing-field"
            htmlFor="SpeakerVoiceSidePanelContent-sample-text"
          >
            <span className="SpeakerVoiceSidePanelContent__design-testing-label">
              Sample Text, to test the voice
            </span>
            <textarea
              id="SpeakerVoiceSidePanelContent-sample-text"
              className="SpeakerVoiceSidePanelContent__design-sample-textarea"
              value={designSampleTestText}
              onChange={handleDesignSampleTextChange}
              onKeyDown={handleDesignSampleTextKeyDown}
              rows={4}
            />
          </label>
          <button
            type="button"
            className={classnames(
              'SpeakerVoiceSidePanelContent__generate-button',
              { 'SpeakerVoiceSidePanelContent__generate-button--disabled': !canGenerateSample }
            )}
            onClick={handleClickGenerateVoiceSample}
            disabled={!canGenerateSample}
          >
            {isGeneratingDesignSample
              ? <span className="SpeakerVoiceSidePanelContent__generate-button-loader" />
              : <React.Fragment>✨ Generate Voice Sample</React.Fragment>
            }
          </button>
          {generatedDesignSampleError && (
            <div className="SpeakerVoiceSidePanelContent__design-generate-error">
              {generatedDesignSampleError}
            </div>
          )}
        </div>
        {(trainingUrls.length > 0) && (
          <div className="SpeakerVoiceSidePanelContent__design-section SpeakerVoiceSidePanelContent__design-section--results">
            <span className="SpeakerVoiceSidePanelContent__design-section-title">
              Results
            </span>
            <div className="SpeakerVoiceSidePanelContent__design-results-list">
              {trainingUrls.map((url, index) => (
                <DesignVoiceSampleSlot
                  key={url}
                  url={url}
                  index={index}
                  regenerateDisabled={isGeneratingDesignSample}
                  onRemoveUrl={handleRemoveDesignSampleUrl}
                  onRegenerateUrl={handleRegenerateDesignSample}
                />
              ))}
            </div>
          </div>
        )}
      </React.Fragment>
    )
  }

  const renderEditContent = () => {

    return (
      <div className="SpeakerVoiceSidePanelContent__content">
        {renderEditHeader()}
        {renderTabSelector()}
        {isSamplesTabSelected ? (
          isDesignEditSamplesView ? (
            renderDesignVoiceFirstStep()
          ) : (
            <TrainingSamples
              draftSpeakerVoice={draftSpeakerVoice}
              originalSpeakerVoiceState={originalSpeakerVoiceState}
              handleDraftChange={handleDraftChange}
              filesToUpload={trainingFilesToUpload}
              setFilesToUpload={setTrainingFilesToUpload}
            />
          )
        ) : (
          <SpeakerVoiceData
            draftSpeakerVoice={draftSpeakerVoice}
            languagesNamesAndCountries={languagesNamesAndCountries}
            handleDraftChange={handleDraftChange}
            enableDesignGenerator={shouldShowInlineDesignGenerator}
            hideStandaloneDescriptionField={shouldHideStandaloneDescriptionField}
            onGenerateSample={handleGenerateDesignSample}
            onRemoveGeneratedSample={handleRemoveGeneratedSample}
            isGeneratingSample={isGeneratingDesignSample}
            generatedSampleError={generatedDesignSampleError}
          />
        )}
        <div className="SpeakerVoiceSidePanelContent__bottom-right">
          <Button
            caption="Save voice"
            onClick={updateSpeakerVoiceFromDraft}
            styleType="brand"
            size="small"
            disabled={shouldDisableContinue}
          />
        </div>
      </div>
    )
  }

  const renderCreateContent = () => {
    const isFirstStep = isSamplesTabSelected
    const currentStep = isFirstStep ? 1 : 2

    return (
      <div className="SpeakerVoiceSidePanelContent__content">
        {renderCreateHeader()}
        {isSamplesTabSelected ? (
          <React.Fragment>
            {renderVoiceStudioCreationToggle()}
            {isDesignCreationFirstStep ? (
              renderDesignVoiceFirstStep()
            ) : (
              <TrainingSamples
                draftSpeakerVoice={draftSpeakerVoice}
                originalSpeakerVoiceState={originalSpeakerVoiceState}
                handleDraftChange={handleDraftChange}
                filesToUpload={trainingFilesToUpload}
                setFilesToUpload={setTrainingFilesToUpload}
              />
            )}
          </React.Fragment>
        ): (
          <div className="SpeakerVoiceSidePanelContent__content-second-step">
            <SpeakerVoiceThumbnail
              draftSpeakerVoice={draftSpeakerVoice}
              originalSpeakerVoiceState={originalSpeakerVoiceState}
              handleDraftChange={handleDraftChange}
              fileToUpload={thumbnailFileToUpload}
              setFileToUpload={setThumbnailFileToUpload}
            />
            <SpeakerVoiceData
              draftSpeakerVoice={draftSpeakerVoice}
              languagesNamesAndCountries={languagesNamesAndCountries}
              handleDraftChange={handleDraftChange}
              enableDesignGenerator={shouldShowInlineDesignGenerator}
              hideStandaloneDescriptionField={shouldHideStandaloneDescriptionField}
              onGenerateSample={handleGenerateDesignSample}
              onRemoveGeneratedSample={handleRemoveGeneratedSample}
              isGeneratingSample={isGeneratingDesignSample}
              generatedSampleError={generatedDesignSampleError}
            />
          </div>
        )}
        <div className="SpeakerVoiceSidePanelContent__bottom-right">
          <StepIndicator
            currentStep={currentStep}
            maxSteps={2}
            stepName={
              isFirstStep
                ? (voiceStudioCreationMode === 'design' ? 'Generate design samples' : 'Upload audio')
                : 'Voice information'
            }
          />
          <Button
            caption={isFirstStep ? 'Continue' : 'Save voice'}
            icon={<Continue />}
            onClick={handleCreateContinue}
            styleType="brand"
            size="small"
            iconRight
            disabled={shouldDisableContinue}
          />
        </div>
      </div>
    )
  }

  return (
    <div
      className={classnames(
        'SpeakerVoiceSidePanelContent',
        { 'SpeakerVoiceSidePanelContent__full': fullSpeakerVoiceSidePanel}
      )}
    >
      {shouldRender && (isNewSpeakerVoice ? renderCreateContent() : renderEditContent())}
    </div>
  )
}

export default SpeakerVoiceSidePanelContent
